import React, { useState, useEffect } from 'react';
import { Heart, AlertTriangle, Shield, CheckCircle, XCircle, UserPlus } from 'lucide-react';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useTheme } from '../contexts/ThemeContext';

function ClientMedicalInfo({ db, client }) {
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConsentForm, setShowConsentForm] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const { theme } = useTheme();

  // Textes des consentements RGPD
  const consentTexts = {
    medicalData: `J'accepte que mes informations médicales (allergies, traitements, niveau d'autonomie) soient traitées par OMAD Services dans le cadre de la prestation de services à domicile. Je comprends que ces données sensibles seront accessibles uniquement aux intervenants concernés par ma prise en charge, dans la stricte mesure nécessaire à l'exécution de leurs missions. Je peux retirer mon consentement à tout moment.`,
    emergencyContact: `Je confirme avoir obtenu l'accord de la personne désignée comme contact d'urgence pour transmettre ses coordonnées à OMAD Services. Ces informations seront utilisées uniquement en cas d'urgence médicale ou de situation nécessitant une intervention rapide.`
  };

  useEffect(() => {
    loadClientData();
  }, [client]);

  const loadClientData = async () => {
    try {
      const clientDoc = await getDoc(doc(db, 'clients', client.id));
      if (clientDoc.exists()) {
        setClientData({
          id: clientDoc.id,
          ...clientDoc.data()
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données médicales:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des données médicales' });
    } finally {
      setLoading(false);
    }
  };

  const handleConsentUpdate = async (consentType, consented) => {
    try {
      const clientRef = doc(db, 'clients', client.id);
      const consentData = {
        consented,
        consentDate: serverTimestamp(),
        consentText: consentTexts[consentType],
        lastUpdated: serverTimestamp()
      };

      await updateDoc(clientRef, {
        [`rgpdConsents.${consentType}`]: consentData,
        updatedAt: serverTimestamp()
      });

      // Recharger les données
      await loadClientData();

      setMessage({ 
        type: 'success', 
        text: consented 
          ? 'Votre consentement a été enregistré.' 
          : 'Votre retrait de consentement a été pris en compte.'
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du consentement:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la mise à jour du consentement' });
    }
  };

  const hasMedicalData = () => {
    return clientData?.allergies || clientData?.treatments || clientData?.autonomyLevel || clientData?.emergencyContact;
  };

  const hasMedicalConsent = () => {
    return clientData?.rgpdConsents?.medicalData?.consented === true;
  };

  const hasEmergencyContactConsent = () => {
    return clientData?.rgpdConsents?.emergencyContact?.consented === true;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement de vos informations médicales...</p>
        </div>
      </div>
    );
  }

  if (!clientData) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="mx-auto text-yellow-500 mb-4" size={48} />
        <p className="text-gray-600 dark:text-gray-400">Impossible de charger vos informations médicales.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Heart className="text-red-600 dark:text-red-400 mr-3" size={24} />
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Informations médicales</h2>
              <p className="text-gray-600 dark:text-gray-400">
                Données sensibles - Traitement soumis à votre consentement explicite (RGPD art. 9)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Message d'état */}
      {message.text && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border-green-200' 
            : 'bg-red-50 text-red-800 border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Section consentements */}
      <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-lg p-6">
        <div className="flex items-start">
          <Shield className="text-yellow-600 dark:text-yellow-400 mr-3 mt-1 flex-shrink-0" size={20} />
          <div>
            <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-2">Consentements RGPD requis</h3>
            <p className="text-yellow-700 dark:text-yellow-300 text-sm">
              Conformément au RGPD, le traitement des données médicales et des contacts d'urgence nécessite votre consentement explicite et séparé.
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          {/* Consentement données médicales */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-yellow-200 dark:border-yellow-600 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-900 dark:text-white">Données médicales</h4>
              {hasMedicalConsent() ? (
                <div className="flex items-center text-green-600 dark:text-green-400">
                  <CheckCircle size={16} className="mr-1" />
                  <span className="text-sm font-medium">Consentement donné</span>
                </div>
              ) : (
                <div className="flex items-center text-red-600 dark:text-red-400">
                  <XCircle size={16} className="mr-1" />
                  <span className="text-sm font-medium">Consentement requis</span>
                </div>
              )}
            </div>
            
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              {consentTexts.medicalData}
            </p>
            
            <div className="flex space-x-3">
              {!hasMedicalConsent() ? (
                <button
                  onClick={() => handleConsentUpdate('medicalData', true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                >
                  Donner mon consentement
                </button>
              ) : (
                <button
                  onClick={() => handleConsentUpdate('medicalData', false)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                >
                  Retirer mon consentement
                </button>
              )}
            </div>
          </div>

          {/* Consentement contact d'urgence */}
          {clientData.emergencyContact && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-yellow-200 dark:border-yellow-600 p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-900 dark:text-white">Contact d'urgence</h4>
                {hasEmergencyContactConsent() ? (
                  <div className="flex items-center text-green-600 dark:text-green-400">
                    <CheckCircle size={16} className="mr-1" />
                    <span className="text-sm font-medium">Consentement donné</span>
                  </div>
                ) : (
                  <div className="flex items-center text-red-600 dark:text-red-400">
                    <XCircle size={16} className="mr-1" />
                    <span className="text-sm font-medium">Consentement requis</span>
                  </div>
                )}
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {consentTexts.emergencyContact}
              </p>
              
              <div className="flex space-x-3">
                {!hasEmergencyContactConsent() ? (
                  <button
                    onClick={() => handleConsentUpdate('emergencyContact', true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    Confirmer l'accord
                  </button>
                ) : (
                  <button
                    onClick={() => handleConsentUpdate('emergencyContact', false)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                  >
                    Retirer l'accord
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Affichage conditionnel des données médicales */}
      {hasMedicalData() && hasMedicalConsent() ? (
        <>
          {/* Allergies */}
          {clientData.allergies && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700">
              <div className="p-6 border-b dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <AlertTriangle className="mr-2 text-orange-600 dark:text-orange-400" size={20} />
                  Allergies
                </h3>
              </div>
              <div className="p-6">
                <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border dark:border-gray-600 text-gray-900 dark:text-white">
                  {clientData.allergies}
                </div>
              </div>
            </div>
          )}

          {/* Traitements */}
          {clientData.treatments && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700">
              <div className="p-6 border-b dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <Heart className="mr-2 text-red-600 dark:text-red-400" size={20} />
                  Traitements en cours
                </h3>
              </div>
              <div className="p-6">
                <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border dark:border-gray-600 text-gray-900 dark:text-white">
                  {clientData.treatments}
                </div>
              </div>
            </div>
          )}

          {/* Niveau d'autonomie */}
          {clientData.autonomyLevel && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700">
              <div className="p-6 border-b dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <UserPlus className="mr-2 text-blue-600 dark:text-blue-400" size={20} />
                  Niveau d'autonomie
                </h3>
              </div>
              <div className="p-6">
                <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border dark:border-gray-600 text-gray-900 dark:text-white">
                  {clientData.autonomyLevel}
                </div>
              </div>
            </div>
          )}

          {/* Contact d'urgence */}
          {clientData.emergencyContact && hasEmergencyContactConsent() && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700">
              <div className="p-6 border-b dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <AlertTriangle className="mr-2 text-red-600 dark:text-red-400" size={20} />
                  Personne à contacter en urgence
                </h3>
              </div>
              <div className="p-6">
                <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border dark:border-gray-600 text-gray-900 dark:text-white">
                  {clientData.emergencyContact}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Ces coordonnées seront utilisées uniquement en cas d'urgence médicale.
                </p>
              </div>
            </div>
          )}
        </>
      ) : hasMedicalData() && !hasMedicalConsent() ? (
        <div className="bg-orange-50 dark:bg-orange-900 border border-orange-200 dark:border-orange-700 rounded-lg p-6 text-center">
          <AlertTriangle className="mx-auto text-orange-500 dark:text-orange-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-orange-800 dark:text-orange-200 mb-2">
            Consentement requis
          </h3>
          <p className="text-orange-700 dark:text-orange-300">
            Vos informations médicales sont disponibles mais nécessitent votre consentement pour être affichées.
          </p>
        </div>
      ) : (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            Aucune information médicale n'est actuellement renseignée.
          </p>
        </div>
      )}
    </div>
  );
}

export default ClientMedicalInfo;
